/**
 * First-party website visitor tracking (SA → CRM → Visitor Analytics).
 *
 * Each browser gets a random visitor id (localStorage "zb_vid") and a session
 * id that rolls over after 30 idle minutes. Page views, clicks and a few key
 * events are queued and sent in batches to /api/site-events as text/plain, so
 * the cross-origin request needs no preflight and survives page unload.
 */

const VISITOR_KEY = "zb_vid";
const SESSION_KEY = "zb_sid";
const OFF_KEY = "zb_track_off";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const FLUSH_DELAY_MS = 4000;
const MAX_BATCH = 25;

// Query params ad networks add to ad clicks. Keep in sync with
// TrafficSourceClassifier::CLICK_IDS on the API.
const AD_CLICK_ID_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ttclid",
  "li_fat_id",
  "twclid",
  "epik",
  "sccid",
  "rdt_cid",
];

let queue = [];
let flushTimer = null;
let firstTouch = null;
let listenersAttached = false;
// Set by VisitorTracker: true only on pages we track (public site, onboarding).
let trackingActive = false;

const isBrowser = () => typeof window !== "undefined";

const storageGet = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const storageSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: tracking just becomes per page load.
  }
};

const uuid = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  // RFC 4122 v4 fallback for older browsers.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    return (char === "x" ? random : (random & 0x3) | 0x8).toString(16);
  });
};

let memoryVisitorId = null;

export function getVisitorId() {
  if (!isBrowser()) return null;
  let id = storageGet(VISITOR_KEY);
  if (!id) {
    id = memoryVisitorId || uuid();
    storageSet(VISITOR_KEY, id);
  }
  memoryVisitorId = id;
  return id;
}

function getSessionId() {
  const now = Date.now();
  let session = null;
  try {
    session = JSON.parse(storageGet(SESSION_KEY) || "null");
  } catch {
    session = null;
  }
  if (!session?.id || now - (session.last || 0) > SESSION_IDLE_MS) {
    session = { id: uuid() };
  }
  session.last = now;
  storageSet(SESSION_KEY, JSON.stringify(session));
  return session.id;
}

/** Turned off for good in this browser once a super admin signs in here. */
export function disableVisitorTracking() {
  if (!isBrowser()) return;
  storageSet(OFF_KEY, "1");
  queue = [];
}

export function isVisitorTrackingDisabled() {
  return !isBrowser() || storageGet(OFF_KEY) === "1";
}

/** True on the main site; tenant booking subdomains belong to businesses' clients. */
export function isMainSiteHost() {
  if (!isBrowser()) return false;
  const host = window.location.hostname;
  const parts = host.split(".");
  let subdomain = "";
  if (host.includes("localhost")) {
    subdomain = parts.length > 1 ? parts[0] : "";
  } else if (parts.length > 2) {
    subdomain = parts[0];
  }
  return ["", "localhost", "zobliss", "www"].includes(subdomain);
}

// Where this visit came from, read once per page load (before client-side
// navigation drops the query string).
function getFirstTouch() {
  if (firstTouch) return firstTouch;
  const params = new URLSearchParams(window.location.search);
  firstTouch = {
    referrer: document.referrer || null,
    landing: window.location.pathname,
    utm: {
      source: params.get("utm_source"),
      medium: params.get("utm_medium"),
      campaign: params.get("utm_campaign"),
      term: params.get("utm_term"),
      content: params.get("utm_content"),
    },
    // Ad click ids (Google, Meta, Microsoft, TikTok...) name the ad platform.
    click_ids: AD_CLICK_ID_PARAMS.filter((param) => params.get(param)),
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    lang: navigator.language || null,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
  };
  return firstTouch;
}

function endpoint() {
  const base = process.env.NEXT_PUBLIC_API_END_POINT || "";
  return `${base.replace(/\/$/, "")}/api/site-events`;
}

export function flushVisitorEvents() {
  if (!isBrowser() || queue.length === 0) return;
  if (isVisitorTrackingDisabled()) {
    queue = [];
    return;
  }
  clearTimeout(flushTimer);
  flushTimer = null;

  const events = queue.splice(0, MAX_BATCH);
  const body = JSON.stringify({
    v: getVisitorId(),
    s: getSessionId(),
    now: Date.now(),
    ctx: getFirstTouch(),
    e: events,
  });

  try {
    fetch(endpoint(), {
      method: "POST",
      body,
      keepalive: true,
      headers: { "Content-Type": "text/plain" },
      credentials: "omit",
    }).catch(() => {});
  } catch {
    // Analytics must never break the page.
  }

  if (queue.length) scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flushVisitorEvents, FLUSH_DELAY_MS);
}

/**
 * Queue one event. `type`: page_view | click | lead | login |
 * onboarding_completed | subscribed | payment_method_added.
 */
export function trackVisitorEvent(type, { path, label, meta } = {}) {
  if (!isBrowser() || !trackingActive || isVisitorTrackingDisabled() || !isMainSiteHost()) return;
  getFirstTouch();
  queue.push({
    t: type,
    p: path ?? window.location.pathname,
    l: label ? String(label).slice(0, 200) : undefined,
    m: meta,
    at: Date.now(),
  });
  if (queue.length >= MAX_BATCH) flushVisitorEvents();
  else scheduleFlush();
}

function clickLabel(element) {
  const explicit =
    element.getAttribute("data-track") ||
    element.getAttribute("aria-label") ||
    element.getAttribute("title");
  const text = explicit || element.innerText || element.textContent || "";
  const label = text.replace(/\s+/g, " ").trim().slice(0, 80);
  if (label) return label;
  const image = element.querySelector("img[alt]");
  return image?.getAttribute("alt") || element.tagName.toLowerCase();
}

function handleClick(event) {
  if (!trackingActive) return;
  const target = event.target instanceof Element ? event.target : null;
  const element = target?.closest(
    "a,button,[role='button'],[role='tab'],[role='menuitem'],summary,[data-track]",
  );
  if (!element) return;

  const href = element.tagName === "A" ? element.getAttribute("href") : null;
  trackVisitorEvent("click", {
    label: clickLabel(element),
    meta: {
      element: element.tagName.toLowerCase(),
      ...(href ? { href: href.slice(0, 200) } : {}),
      ...(element.closest("header,nav") ? { area: "header" } : {}),
      ...(element.closest("footer") ? { area: "footer" } : {}),
    },
  });
}

/** Attach the click, unload and tab-hide listeners once. */
export function startVisitorTracker() {
  if (!isBrowser() || listenersAttached) return;
  listenersAttached = true;
  document.addEventListener("click", handleClick, true);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushVisitorEvents();
  });
  window.addEventListener("pagehide", flushVisitorEvents);
}

/**
 * Turns recording on for pages the tracker covers (public pages, onboarding)
 * and off elsewhere, e.g. inside the business panel after login.
 */
export function setVisitorTrackingActive(enabled) {
  trackingActive = Boolean(enabled);
}

// gtag events that are worth keeping in the visitor journey too. Trial
// sign-ups and payments are recorded by the API, not here.
const GTAG_EVENT_MAP = {
  // "Book a demo" / "Talk to us" → the visitor's demo request.
  generate_lead: "lead",
  login: "login",
  tutorial_complete: "onboarding_completed",
  subscription_purchase: "subscribed",
  add_payment_info: "payment_method_added",
};

/** Called from lib/gtag trackEvent so existing analytics calls feed the journey. */
export function forwardGtagEvent(eventName, params = {}) {
  const type = GTAG_EVENT_MAP[eventName];
  if (!type) return;
  const meta = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (["string", "number", "boolean"].includes(typeof value)) meta[key] = value;
  });
  trackVisitorEvent(type, { label: params?.source || params?.plan || eventName, meta });
  flushVisitorEvents();
}
